<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >
    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('CREATE_CARD_PROFILE')"
            :label="$t('Add')"
            size="md"
            @click="showModal('modal-lg')"
          />
          <b-dropdown
            id="dropdown-1"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            class="ml-1"
            :text="$t('Export')"
            variant="primary"
          >
            <b-dropdown-item @click="csvExport">
              {{ $t('CSV') }}
            </b-dropdown-item>
            <b-dropdown-item @click="excelExport">
              {{ $t('Excel') }}
            </b-dropdown-item>
          </b-dropdown>
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="cardProfiles"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :items="cardProfileList"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(dateCreated)="data">
        {{ toDateWithYear(data.value) }}
      </template>

      <template #cell(status)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">

        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >

            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('MODIFY_CARD_PROFILE')"
              @click="onSelectCardProfile(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50"> {{ $t('Modify') }} </span>
            </b-dropdown-item>

            <b-dropdown-item @click="onSelectCardProfile(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50"> {{ $t('View') }} </span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="getUserPermission(data.item.status === 'Active' ? 'DEACTIVATE_CARD_PROFILE' : 'ACTIVATE_CARD_PROFILE')"
              @click="cardProfileProfileStatus(data.item, $event.target)"
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{ data.item.status === 'Active' ? $t('Deactivate') : $t('Activate') }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      :ok-title="buttonLabel"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      :ok-disabled="isLoading"
      centered
      no-close-on-backdrop
      size="lg"
      :title="modalTitle"
      @ok="handleSubmit"
      @hidden="resetModal"
    >
      <validation-observer ref="cardProfilesRules">
        <b-form>
          <b-row class="match-height">

            <!--   Card Profile Name -->
            <b-col
              md="6"
            >
              <InputField
                id="card-profile-name"
                v-model="cardProfilesFormData.name "
                :label="$t('Card Profile Name')"
                type="text"
                :error-name="$t('Card Profile Name')"
                rules="required"
                :readonly="isReadonly"
              />
            </b-col>
            <!--  Card Profile Name -->

            <!-- Card Program Name -->
            <b-col
              md="6"
            >
              <InputField
                id="card-program-name"
                v-model="cardProfilesFormData.cardProgram"
                :label="$t('Card Program Name')"
                :placeholder="$t('Enter Card Program Name')"
                type="text"
                :error-name="$t('Card Program Name')"
                rules="required"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Card Program Name -->

            <!-- Host Financial Institution -->
            <b-col md="6">
              <SelectField
                id="host-financial-institution"
                v-model="cardProfilesFormData.hostFIId"
                :label="$t('Host Financial Institution')"
                :placeholder="$t('Select')"
                :options="hostFinancialInstitutionList"
                item="label"
                :reduce="option => option.value"
                :loading="isLoadingHostFI"
                :error-name="$t('Host Financial Institution')"
                rules="required"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Host Financial Institution -->

            <!-- Card Type -->
            <b-col md="6">
              <SelectField
                id="card-type"
                v-model="cardProfilesFormData.scheme"
                :label="$t('Scheme')"
                :placeholder="$t('Select')"
                :options="cardTypesOptions"
                item="label"
                :reduce="option => option.value"
                :error-name="$t('Scheme')"
                rules="required"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Card Type -->

            <!-- Card BIN -->
            <b-col
              md="6"
            >
              <InputField
                id="ftp-location"
                v-model="cardProfilesFormData.bin"
                :label="$t('Card BIN')"
                :placeholder="$t('Enter Card BIN')"
                type="number"
                :error-name="$t('Card BIN')"
                rules="required"
                :readonly="isEditable"
              />
            </b-col>
            <!-- Card BIN -->

            <!-- Card Life Span (Number of Months)-->
            <b-col md="6">
              <SelectField
                id="card-type"
                v-model="cardProfilesFormData.numberOfMonthsBeforeExpiry"
                :label="$t('Card Life Span (Number of Months)')"
                :placeholder="$t('Select')"
                :options="cardLifeSpanOptions"
                item="label"
                :reduce="option => option"
                :error-name="$t('Card Life Span')"
                rules="required"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Card Life Span (Number of Months) -->
          </b-row>
        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      no-close-on-backdrop
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchCardProfiles"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Card Profile Name -->
        <b-col md="6">
          <InputField
            id="card-profile-name"
            v-model="filter.name"
            :label="$t('Card Profile Name')"
            :placeholder="$t('Enter Card Profile Name')"
            type="text"
          />
        </b-col>
        <!-- Card Profile Name -->

        <!-- Card Program Name -->
        <b-col md="6">
          <InputField
            id="card-program-name"
            v-model="filter.cardProgram"
            :label="$t('Card Program Name')"
            :placeholder="$t('Enter Card Program Name')"
            type="text"
          />
        </b-col>
        <!-- Card Program Name -->

        <!-- Host Financial Institution -->
        <b-col md="6">
          <SelectField
            id="host-financial-institution"
            v-model="filter.hostFIId"
            :label="$t('Host Financial Institution')"
            :placeholder="$t('Select')"
            :options="hostFinancialInstitutionList"
            item="label"
            :reduce="option => option.value"
            :loading="isLoadingHostFI"
          />
        </b-col>
        <!-- Host Financial Institution -->
        <!-- Card Type -->
        <b-col md="6">
          <SelectField
            id="scheme"
            v-model="filter.scheme"
            :label="$t('Scheme')"
            :placeholder="$t('Select')"
            :options="cardTypesOptions"
            item="label"
            :reduce="option => option.value"
          />
        </b-col>
        <!-- Card Type -->
        <!--Card BIN -->
        <b-col md="6">
          <InputField
            id="card-bin"
            v-model="filter.bin"
            :label="$t('Card BIN')"
            :placeholder="$t('Enter Card BIN')"
            type="number"
          />
        </b-col>
        <!--Card BIN -->

        <!-- Status -->
        <b-col md="6">
          <SelectField
            id="status"
            v-model="filter.status"
            :label="$t('Status')"
            :placeholder="$t('Select')"
            :options="statusOptions"
            item="label"
            :reduce="option => option"
          />
        </b-col>
        <!-- Status -->
      </b-row>
    </b-modal>
    <!-- Filter modal -->
    <FullScreenSpinner :show="showSpinner" />
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BBadge, BModal, BForm, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { mapGetters, mapActions } from 'vuex'
import { required, minPhoneVal, maxPhoneVal } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import Ripple from 'vue-ripple-directive'
import { heightFade } from '@core/directives/animations'
import { permissionMixin } from '@core/mixins/permission'
import Button from '../../../components/button/Button.vue'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import InputField from '../../../forms/InputField.vue'
import SelectField from '../../../forms/SelectField.vue'
import { gender, accessLevel, cardTypes } from '@/helpers/data'
import getStatusColor from '@/helpers/getStatusColor'
import { toDateWithYear } from '@/helpers/formatDate'
import { eChannelsPermission } from '@/helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BDropdown,
    BDropdownItem,
    BBadge,
    TablePagination,
    BModal,
    BForm,
    BSpinner,
    TableEntries,
    SelectField,
    ValidationObserver,
    InputField,
    Button,
    FullScreenSpinner,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toDateWithYear,
      isReadonly: false,
      isEditable: false,
      isFullName: false,
      showSpinner: false,
      isLoadingHostFI: false,
      sortBy: 'name',
      required,
      minPhoneVal,
      maxPhoneVal,
      modalTitle: this.$t('Card Profile Setup'),
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'name', sortable: true, label: 'Card Profile Name' },
        { key: 'cardProgram', sortable: true, label: 'Card Program Name' },
        { key: 'hostFIName', sortable: true, label: 'Host Financial Institution' },
        { key: 'bin', sortable: true, label: 'Card BIN' },
        { key: 'numberOfMonthsBeforeExpiry', sortable: true, label: 'Card Life Span' },
        { key: 'status', sortable: true, label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ],
      genderOptions: gender,
      getStatusColor,
      buttonLabel: this.$t('Save'),
      cardProfilesFormData: {
        name: '',
        bin: '',
        scheme: '',
        hostFIId: '',
        numberOfMonthsBeforeExpiry: '',
        cardProgram: '',
      },
      filter: {
        name: '',
        bin: '',
        scheme: '',
        hostFIId: '',
        numberOfMonthsBeforeExpiry: '',
        cardProgram: '',
        status: '',
        sortColumn: '',
      },
      cardLifeSpanOptions: ['12', '24', '36', '48', '60'],
      datePickerConfig: {
        dateFormat: 'm/Y',
        altFormat: 'm/Y',
        ariaDateFormat: 'm/Y',
        minDate: 'today',
        defaultDate: '',
      },
      accessLevelOptions: accessLevel,
      cardTypesOptions: cardTypes,
      cardProfileList: [],
      hostFinancialInstitutionList: [],
    }
  },
  computed: {
    ...mapGetters({
      branchOptions: 'operations/branchOptions',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchCardProfiles()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchCardProfiles()
    },
  },
  mounted() {
    this.fetchCardProfiles()
    this.fetchHostFinancialInstitution()
  },
  methods: {
    ...mapActions({
      getCardProfileList: 'eChannels/getCardProfileList',
      updateCardProfile: 'eChannels/updateCardProfileAction',
      createCardProfile: 'eChannels/createCardProfileAction',
      updateCardProfileStatus: 'eChannels/updateStatus',
      getHostFIList: 'eChannels/getHostFIList',
      downloadCardProfileReport: 'reports/downloadCardProfileReport',
    }),
    async fetchCardProfiles() {
      try {
        this.isBusy = true
        this.hideModal('modal-filter')
        const response = await this.getCardProfileList({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: eChannelsPermission.VIEW_CARD_PROFILE,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.cardProfileList = data.data
          this.totalCount = data.recordCount
        } else {
          this.cardProfileList = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async fetchHostFinancialInstitution() {
      try {
        this.isLoadingHostFI = true
        const { status, data } = await this.getHostFIList({
          search: {},
          page: 1,
          limit: 200,
          permission: eChannelsPermission.VIEW_CARD_PROFILE,
        })
        if (status === 200 && data.data) {
          this.hostFinancialInstitutionList = data.data.filter(item => item.status !== null && item.status.toLowerCase() === 'active').map(item => ({ label: item.name, value: item.id }))
        } else {
          this.hostFinancialInstitutionList = []
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isLoadingHostFI = false
      }
    },
    cardProfileProfileStatus(items) {
      const { id, status } = items
      const action = status === 'Active' ? this.$t('Deactivate') : this.$t('Activate')
      const cardStatus = status === 'Active' ? 'InActive' : 'Active'
      const permission = status === 'Active' ? eChannelsPermission.DEACTIVATE_CARD_PROFILE : eChannelsPermission.ACTIVATE_CARD_PROFILE

      const updateStatus = async () => {
        const requesttext = this.$t('Your request to')
        try {
          this.showSpinner = true
          const statusValues = {
            id,
            status: cardStatus,
          }
          const response = await this.updateCardProfileStatus({
            statusValues, routeName: 'CardProfile', permission,
          })
          if (response.status === 200) {
            this.fetchCardProfiles()
            const succesText = this.$t('Card Profile was successful')
            this.alertSuccess({ text: `${requesttext} ${action} ${succesText}` })
            this.showSpinner = false
          }
        } catch (error) {
          const reasonText = this.$t('Card Profile failed. Reason:')
          this.alertError({ text: `${requesttext} ${action} ${reasonText} ${error}` })
        } finally {
          this.showSpinner = false
        }
      }
      const text1 = this.$t('Are you sure you want to')
      const text2 = this.$t('this Card Profile?')
      this.confirm(updateStatus, { title: '', text: `${text1} ${action} ${text2}` })
    },
    handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.cardProfilesRules.validate().then(success => {
        if (success) {
          const text1 = this.$t('Are you sure you want to')
          const text2 = this.$t('this Card Profile?')
          this.confirm(this.cardProfileAction, { text: `${text1} ${this.buttonLabel} ${text2}` })
        }
      })
    },
    async cardProfileAction() {
      try {
        this.isLoading = true
        const response = this.buttonLabel === 'Save'
          ? await this.createCardProfile({
            ...this.cardProfilesFormData,
          })
          : await this.updateCardProfile({
            name: this.cardProfilesFormData.name,
            hostFIId: this.cardProfilesFormData.hostFIId,
            cardProgram: this.cardProfilesFormData.cardProgram,
            scheme: this.cardProfilesFormData.scheme,
            numberOfMonthsBeforeExpiry: this.cardProfilesFormData.numberOfMonthsBeforeExpiry,
            id: this.cardProfilesFormData.id,
          })
        const { status } = response
        if (status === 200) {
          this.fetchCardProfiles()
          this.hideModal('modal-lg')
          const requesttext = this.$t('Your request to')
          const succesText = this.$t('Card Profile was successful')
          this.alertSuccess({ text: `${requesttext} ${this.buttonLabel} ${succesText}` })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
      }
    },
    async onSelectCardProfile(items, activity, val) {
      this.cardProfilesFormData = {
        ...items,
        id: items.id,
        hostFIId: items.hostFIId,
      }
      this.datePickerConfig = {
        ...this.datePickerConfig,
        minDate: '',
      }
      this.isReadonly = val === 'edit' ? this.isReadonly = false : this.isReadonly = true
      this.isEditable = true
      this.modalTitle = val === 'edit' ? this.$t('Edit Card Profile') : this.$t('Card Profile')
      this.buttonLabel = this.$t('Update')
      this.$root.$emit('bv::show::modal', 'modal-lg')
    },
    async csvExport() {
      try {
        this.isBusy = true
        const response = await this.downloadCardProfileReport({
          search: this.filter,
          fileExportFormat: 1,
          permission: eChannelsPermission.VIEW_CARD_PROFILE,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: this.$t('Your download is in progress and might take a little longer. Go to your'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('to access the completed file.'),
          })
        } else if (response.status === 504) {
          this.alertSuccessCustom({
            text: this.$t('Your download request is still processing, keep checking the'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('for status.'),
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async excelExport() {
      try {
        this.isBusy = true
        const response = await this.downloadCardProfileReport({
          search: this.filter,
          fileExportFormat: 0,
          permission: eChannelsPermission.VIEW_CARD_PROFILE,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: this.$t('Your download is in progress and might take a little longer. Go to your'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('to access the completed file.'),
          })
        } else if (response.status === 504) {
          this.alertSuccessCustom({
            text: this.$t('Your download request is still processing, keep checking the'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('for status.'),
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    resetModal() {
      this.cardProfilesFormData = { ...null }
      this.isLoading = false
      this.isEditable = false
      this.isReadonly = false
      this.buttonLabel = this.$t('Save')
      this.modalTitle = this.$t('Create Card Profile')
      this.datePickerConfig = {
        ...this.datePickerConfig,
        minDate: 'today',
      }
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchCardProfiles()
    },
  },
}
</script>
